<template>

  <SubHeading :home="home" :tabs="tabs" :shorts="false" :currTab="currTab" @active="handleActive" />

  <!-- breadcrumbs -->
  <nav class="sba-collection-breadcrumb enrollee-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text pointer">
          <span>{{ enrolleeBreadcrumbTitle }}</span>
        </span>
        <span class="sba-collection-context">Monthly Enrollee Register</span>
      </div>
    </div>
  </nav>

  <!-- Top -->
  <div id="top"></div>

  <div class="container enrollee-page-shell">
    <div class="enrollee-workbench sba-slide-in">
      <div class="enrollee-review-head">
        <div class="enrollee-title-row">
          <div class="enrollee-title-block">
            <label class="active teal-text">Enrollee List</label>
            <h5>Active Monthly Enrollees</h5>
            <p>Search and review the active monthly enrollee register.</p>
          </div>
          <button type="button" class="search-toggle" @click="toggleSearchFilters">
            <span>{{ showSearchFilters ? "Hide Search" : "Show Search" }}</span>
            <i class="material-icons">{{ showSearchFilters ? "expand_less" : "tune" }}</i>
          </button>
        </div>
      </div>

      <div class="enrollee-filter-shell" v-show="showSearchFilters">
        <div class="enrollee-sync-strip">
          <div class="enrollee-sync-actions">
            <button
              v-if="canResetEnrolleeSync"
              type="button"
              class="enrollee-sync-button enrollee-reset-button"
              :class="{ 'is-syncing': syncMode === 'reset' }"
              :disabled="isSyncing || !currUrl"
              :title="currUrl ? 'Reset current month from a fresh LifeWORTH payload' : 'Run a search before resetting'"
              @click="handleResetEnrollees"
            >
              <i class="material-icons">restart_alt</i>
              <span>{{ resetButtonLabel }}</span>
            </button>
            <button
              type="button"
              class="enrollee-sync-button"
              :class="{ 'is-syncing': syncMode === 'sync' }"
              :disabled="isSyncing || !currUrl"
              :title="currUrl ? 'Pull enrollee list from external source' : 'Run a search before syncing'"
              @click="handleSyncEnrollees"
            >
              <i class="material-icons">sync</i>
              <span>{{ syncButtonLabel }}</span>
            </button>
          </div>
          <div class="enrollee-sync-meta">
            <span>Last synced</span>
            <strong>{{ lastSyncedLabel }}</strong>
          </div>
        </div>
        <div class="enrollee-sync-status" v-if="syncMessage || syncError" :class="{ 'has-error': syncError }">
          {{ syncError || syncMessage }}
        </div>
        <div class="enrollee-filter-card">
          <FilterEnrollee @search="handleSearch" />
        </div>
      </div>

      <div class="enrollee-search-summary" :class="{ 'has-loader': isLoading }">
        <span class="summary-label">Current view</span>
        <div class="summary-content" v-if="searchTitle" v-html="searchTitle"></div>
        <div class="summary-content" v-else>
          <span class="summary-chip">All active enrollees</span>
        </div>
      </div>

      <div class="progress enrollee-progress" v-if="isReady && isLoading">
        <div class="indeterminate"></div>
      </div>

      <LoadingDiv v-if="!isReady" />

      <div class="enrollee-list-card" v-if="isReady && enrollees.length">
        <div class="enrollee-table-head hide-on-med-and-down" v-if="enrollees.length">
          <span>No.</span>
          <span>Member</span>
          <span>Client</span>
          <span>Provider</span>
          <span>Period</span>
        </div>
        <div class="enrollee-row-card" v-for="(enrollee, index) in enrollees" :key="enrollee.id">
          <div class="enrollee-row-grid">
            <div class="hide-on-small-only">
              <span class="enrollee-number">{{ numberBadge(index) }}</span>
            </div>
            <div class="enrollee-person">
              <h6>
                <span>{{ enrollee.name.toUpperCase() }}</span>
                <span
                  class="enrollee-type-text"
                  :class="enrolleeTypeMeta(enrollee.type).className"
                >
                  {{ enrolleeTypeMeta(enrollee.type).label }}
                </span>
              </h6>
              <div class="enrollee-contact-line">
                <span v-if="ageSex(enrollee.dob, enrollee.sex)">
                  {{ ageSex(enrollee.dob, enrollee.sex)?.toUpperCase() }}
                </span>
                <span class="contact-dot"></span>
                <span>{{ enrollee.phone || "No phone recorded" }}</span>
              </div>
              <div class="enrollee-id-line">
                <span>Enrollee ID</span>
                <strong>{{ enrollee.enrollee_number }}</strong>
              </div>
            </div>
            <div class="enrollee-data-cell">
              <span>Client</span>
              <p class="enrollee-client">{{ enrollee.client_name || "No client recorded" }}</p>
            </div>
            <div class="enrollee-data-cell">
              <span>Provider</span>
              <p class="enrollee-provider" v-if="enrollee.picked_provider">{{ enrollee.picked_provider }}</p>
              <p class="enrollee-provider" v-else>Roaming</p>
            </div>
            <div class="enrollee-data-cell hide-on-small-only">
              <span>Period</span>
              <p class="enrollee-period">
                <span>
                  {{
                    new Date(listMonth).toLocaleString(undefined, {
                      year: "numeric",
                      month: "short",
                    })
                  }}
                </span>
                <span class="enrollee-period-flag" v-if="partialMonthEndLabel(enrollee)">
                  {{ partialMonthEndLabel(enrollee) }}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="enrollee-empty-state" v-if="isReady && !isLoading && !enrollees.length">
        <h6><img src="@/assets/img/no-user.png" width="192" class="circle responsive-img" /></h6>
        <p>No enrollee record matched the current selection.</p>
      </div>

      <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-else-if="enrollees.length" />
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useStore } from "vuex";
import useModuleNavigation from "@/composables/useModuleNavigation";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import FilterEnrollee from "@/components/enrollee/FilterEnrolleeComponent.vue";
import { getCache, addCache } from "@/composables/cache/cache";
import { ABBR } from "@/laravel/config";


export default {
  name: "enrolleeHome",
  components: { SubHeading, LoadingDiv, PaginationDiv, FilterEnrollee },
  setup() {
    const store = useStore();
    const response = ref(null);
    const listMonth = ref(null);
    const searchTitle = ref(null);
    const isReady = ref(false);
    const isLoading = ref(true);
    const error = ref(null);
    const currUrl = ref(null);
    const currHook = ref(null);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);
    const showSearchFilters = ref(false);
    const isSyncing = ref(false);
    const syncMode = ref(null);
    const syncMessage = ref(null);
    const syncError = ref(null);
    const lastSyncedAt = ref(localStorage.getItem("lifeworth.enrollee.lastSyncedAt"));
    let isDisposed = false;

    const enrolleeBreadcrumbTitle = computed(() => {
      return ABBR ? `${ABBR} Enrollees Home` : "Enrollees Home";
    });

    const lastSyncedLabel = computed(() => {
      if (!lastSyncedAt.value) {
        return "Not yet";
      }

      const date = new Date(lastSyncedAt.value);

      if (Number.isNaN(date.getTime())) {
        return "Not yet";
      }

      return date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    });

    const syncButtonLabel = computed(() => {
      return isSyncing.value ? "Syncing list..." : "Sync enrollee list";
    });

    const resetButtonLabel = computed(() => {
      return syncMode.value === "reset" ? "Resetting..." : "Reset month";
    });

    const canResetEnrolleeSync = computed(() => {
      return (store.state.user?.modules || []).includes("compliance");
    });

    // vue router
    const { routeToTab } = useModuleNavigation();

    // start
    onMounted(() => {
      // page name
      document.title = "Active (Monthly) Enrollees";
    });

    onBeforeUnmount(() => {
      isDisposed = true;
    });

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Enrollees", title: "All", icon: null, url: "enrollee", hash: null, });
      tabs.value.push({ id: 2, name: "Enrollees", title: "Add New", icon: null, url: "enrolleeAdd", hash: null, });

      // set default
      currTab.value = tabs.value.find(x => x.id === 1);
      // set home
      home.value = tabs.value.find(x => x.id === 1);
    });

    // handle curr status
    const handleActive = (tab) => {
      routeToTab(tab, { replace: tab?.url === "enrollee" });
    };

    const toggleSearchFilters = () => {
      showSearchFilters.value = !showSearchFilters.value;
    };

    // get function
    const getItemsFromApi = async (table, options = {}) => {
      const useCache = options.useCache !== false;
      const completeLoading = options.completeLoading !== false;

      // get item from store (cache)
      let cache = useCache ? getCache(table) : null;

      if (cache?.length) {
        response.value = cache;
      }

      const { res, err } = await getItems(table);

      if (res.value) {
        response.value = res.value.data;
        // add cache
        addCache(table, response.value);
      } else {
        error.value = err.value;
      }

      if (completeLoading) {
        // page is ready, loading is complete
        isReady.value = true;
        isLoading.value = false;
      }

      return response.value;
    };

    // numbering
    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    // current month, year
    const getCurrentMonthYear = () => {
      return `${new Date().toLocaleDateString("en", {
        month: "long",
      })} ${new Date().getFullYear()}`;
    };

    // display function
    const ageSex = (dob, sex) => {
      let ageSex;
      let birthDateObj = new Date(dob);
      let today = new Date();
      let age = today.getFullYear() - birthDateObj.getFullYear();

      if (
        today.getMonth() < birthDateObj.getMonth() ||
        (today.getMonth() === birthDateObj.getMonth() &&
          today.getDate() < birthDateObj.getDate())
      ) {
        age--;
      }

      if (dob && sex) {
        ageSex = `(${sex}/${age})`;
      } else if (dob) {
        ageSex = `(${age}yrs)`;
      } else if (sex) {
        ageSex = `(${sex})`;
      }

      return ageSex;
    };

    const enrolleeTypeMeta = (type) => {
      const types = {
        p: { label: "Principal", className: "is-principal" },
        s: { label: "Spouse", className: "is-spouse" },
        d: { label: "Dependant", className: "is-dependant" },
      };

      return types[type] || { label: "Unclassified", className: "is-unclassified" };
    };

    const parseDateOnly = (value) => {
      if (!value) {
        return null;
      }

      const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);

      return Number.isNaN(date.getTime()) ? null : date;
    };

    const partialMonthEndLabel = (enrollee) => {
      const periodDate = parseDateOnly(listMonth.value);
      const endDate = parseDateOnly(enrollee?.new_end_date);

      if (!periodDate || !endDate) {
        return null;
      }

      const monthEnd = new Date(
        periodDate.getFullYear(),
        periodDate.getMonth() + 1,
        0
      );

      const endsInSelectedMonth =
        endDate.getFullYear() === periodDate.getFullYear() &&
        endDate.getMonth() === periodDate.getMonth();

      if (!endsInSelectedMonth || endDate >= monthEnd) {
        return null;
      }

      return `Till ${endDate.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
      })}`;
    };

    // handle search (emitted from component)
    const handleSearch = (val) => {
      // start loading
      isLoading.value = true;
      syncError.value = null;
      // set current Url
      currUrl.value = val.currUrl;
      // set current Hook
      currHook.value = val.currHook;
      // set listMonth
      listMonth.value = val.listMonth;
      // set search title
      searchTitle.value = val.searchTitle;
      // get items from function
      getItemsFromApi(currUrl.value);
    };

    // switch page
    const handlePage = (page) => {
      // start laoding
      isLoading.value = true;
      // go back up
      scrollToPageTop();
      // get new page items from function
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`);
    };

    const delay = (duration) => {
      return new Promise((resolve) => {
        setTimeout(resolve, duration);
      });
    };

    const getFirstPageUrl = () => {
      if (!currUrl.value) {
        return null;
      }

      return `${currUrl.value}${currHook.value || "?"}page=1`;
    };

    const getQueryValue = (url, key) => {
      const query = String(url || "").split("?")[1] || "";

      return new URLSearchParams(query).get(key);
    };

    const getSyncPayload = (payload) => {
      return payload?.data || payload || {};
    };

    const getSyncStatusUrl = (payload) => {
      const syncPayload = getSyncPayload(payload);

      if (syncPayload.status_url || syncPayload.poll_url) {
        return syncPayload.status_url || syncPayload.poll_url;
      }

      if (syncPayload.sync_id) {
        return `custom/lifeworth/enrollees/sync/status/${syncPayload.sync_id}`;
      }

      return null;
    };

    const getSyncStatus = (payload) => {
      const syncPayload = getSyncPayload(payload);

      return String(syncPayload.status || syncPayload.sync_status || "").toLowerCase();
    };

    const isSyncComplete = (payload) => {
      return ["completed", "complete", "done", "success"].includes(getSyncStatus(payload));
    };

    const isSyncFailed = (payload) => {
      return ["failed", "error", "cancelled", "canceled"].includes(getSyncStatus(payload));
    };

    const markSynced = (value) => {
      const syncedAt = value || new Date().toISOString();

      lastSyncedAt.value = syncedAt;
      localStorage.setItem("lifeworth.enrollee.lastSyncedAt", syncedAt);
    };

    const getSyncedAt = (payload) => {
      const syncPayload = getSyncPayload(payload);

      return syncPayload.last_synced_at || syncPayload.synced_at || syncPayload.completed_at;
    };

    const refreshFirstPageAfterSync = async () => {
      const firstPageUrl = getFirstPageUrl();

      if (!firstPageUrl) {
        return null;
      }

      scrollToPageTop();
      return getItemsFromApi(firstPageUrl, {
        useCache: false,
      });
    };

    const pollSyncStatus = async (statusUrl) => {
      const maxAttempts = 18;

      if (!statusUrl) {
        syncError.value = "Laravel did not return a sync status URL.";
        return null;
      }

      for (let attempt = 1; attempt <= maxAttempts && !isDisposed; attempt++) {
        syncMessage.value = `Sync running (${attempt}/${maxAttempts})...`;

        if (attempt > 1) {
          await delay(2500);
        }

        const { res, err } = await getItems(statusUrl);

        if (err.value) {
          syncError.value = err.value;
          return null;
        }

        const statusPayload = getSyncPayload(res.value);

        if (isSyncFailed(statusPayload)) {
          syncError.value = statusPayload.message || "Sync failed before the enrollee list could be refreshed.";
          return null;
        }

        if (isSyncComplete(statusPayload)) {
          return statusPayload;
        }
      }

      return null;
    };

    const syncPayloadForCurrentView = () => {
      return {
        list_month: listMonth.value,
        scheme_id: getQueryValue(currUrl.value, "s"),
        current_url: currUrl.value,
        first_page_url: getFirstPageUrl(),
      };
    };

    const runManualEnrolleeSync = async ({ mode, endpoint, startMessage, doneMessage }) => {
      if (!currUrl.value) {
        syncError.value = "Run a search first, then sync the current enrollee list.";
        return;
      }

      isSyncing.value = true;
      syncMode.value = mode;
      isLoading.value = true;
      syncError.value = null;
      syncMessage.value = startMessage;

      const { res, err } = await postItem(endpoint, syncPayloadForCurrentView());

      if (err.value) {
        isSyncing.value = false;
        syncMode.value = null;
        isLoading.value = false;
        syncMessage.value = null;
        syncError.value = err.value;
        return;
      }

      const syncPayload = res.value?.data || res.value || {};
      const completedPayload = isSyncComplete(syncPayload)
        ? syncPayload
        : await pollSyncStatus(getSyncStatusUrl(syncPayload));

      if (completedPayload) {
        syncMessage.value = "Refreshing enrollee list...";
        await refreshFirstPageAfterSync();
        markSynced(getSyncedAt(completedPayload));
        syncMessage.value = doneMessage;
      } else {
        isLoading.value = false;
        syncError.value = syncError.value || "Sync started, but Laravel did not confirm completion yet. Try again shortly.";
      }

      isSyncing.value = false;
      syncMode.value = null;
    };

    const handleSyncEnrollees = async () => {
      await runManualEnrolleeSync({
        mode: "sync",
        endpoint: "custom/lifeworth/enrollees/sync",
        startMessage: "Starting external enrollee pull...",
        doneMessage: "Enrollee list synced.",
      });
    };

    const handleResetEnrollees = async () => {
      if (!window.confirm("Reset this month from LifeWORTH? The current month list will only be deleted after Laravel confirms a fresh remote payload is available. Use the sync button instead if you are not sure.")) {
        return;
      }

      await runManualEnrolleeSync({
        mode: "reset",
        endpoint: "custom/lifeworth/enrollees/sync/reset",
        startMessage: "Fetching fresh LifeWORTH payload before reset...",
        doneMessage: "Current month enrollee list reset.",
      });
    };

    const scrollToPageTop = () => {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
    };

    // output results
    const enrollees = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    return {
      handleActive,
      toggleSearchFilters,
      home,
      currTab,
      tabs,
      showSearchFilters,
      enrolleeBreadcrumbTitle,
      ageSex,
      enrolleeTypeMeta,
      partialMonthEndLabel,
      numberBadge,
      getCurrentMonthYear,
      handleSearch,
      handlePage,
      handleSyncEnrollees,
      handleResetEnrollees,
      isReady,
      isLoading,
      isSyncing,
      syncMode,
      canResetEnrolleeSync,
      error,
      syncMessage,
      syncError,
      syncButtonLabel,
      resetButtonLabel,
      lastSyncedLabel,
      currUrl,
      listMonth,
      searchTitle,
      enrollees,
      allPages,
    };
  },
};
</script>

<style scoped>
.enrollee-page-shell {
  margin-top: -14px;
  padding-top: 0;
  padding-bottom: 44px;
  position: relative;
  z-index: 1;
}

.enrollee-workbench {
  color: #263238;
}

.enrollee-review-head,
.enrollee-filter-shell,
.enrollee-search-summary,
.enrollee-progress,
.enrollee-list-card,
.enrollee-row-card,
.enrollee-empty-state {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.enrollee-review-head,
.enrollee-filter-shell,
.enrollee-search-summary,
.enrollee-list-card,
.enrollee-row-card,
.enrollee-table-head {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  box-sizing: border-box;
  box-shadow: none;
  width: 100%;
}

.enrollee-review-head {
  align-items: flex-end;
  background: #ffffff;
  border-color: #c4d3d9;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  display: flex;
  gap: 22px;
  justify-content: space-between;
  margin-bottom: 0;
  overflow: visible;
  padding: 24px 26px;
  position: relative;
  z-index: 2;
}

.enrollee-review-head::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 14px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.enrollee-title-row {
  align-items: flex-end;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  position: relative;
  width: 100%;
  z-index: 2;
}

.enrollee-title-block h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.enrollee-title-block p {
  color: #607d8b;
  line-height: 1.45;
  margin: 6px 0 0;
  max-width: 820px;
}

.search-toggle {
  align-items: center;
  background: #ffffff;
  border: 1px solid #cfd8dc;
  border-radius: 999px;
  color: #263238;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 8px;
  min-height: 40px;
  padding: 0 13px 0 16px;
}

.search-toggle i {
  color: #00897b;
  font-size: 19px;
}

.enrollee-search-summary {
  align-items: center;
  background: #f7f7f7;
  border-color: #c4d3d9;
  border-top: 0;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  box-shadow: 0 12px 22px -24px rgba(38, 50, 56, 0.68);
  display: flex;
  gap: 14px;
  justify-content: space-between;
  padding: 14px 24px;
}

.enrollee-search-summary.has-loader {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.enrollee-filter-shell {
  border-color: #c4d3d9;
  border-radius: 0;
  border-top: 0;
  box-shadow: 0 14px 24px -24px rgba(38, 50, 56, 0.68);
  padding: 18px 24px 22px;
}

.enrollee-sync-strip {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #c7d3d8;
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  min-height: 58px;
  padding: 10px 14px;
}

.enrollee-sync-button {
  align-items: center;
  background: #ffffff;
  border: 1px solid #b7c8cf;
  border-radius: 6px;
  color: #263238;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.84rem;
  font-weight: 800;
  gap: 9px;
  min-height: 38px;
  padding: 0 13px 0 11px;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.enrollee-sync-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.enrollee-sync-button:hover:not(:disabled) {
  background: #e0f2f1;
  border-color: #00897b;
  color: #00695c;
}

.enrollee-reset-button i {
  color: #ef6c00;
}

.enrollee-reset-button:hover:not(:disabled) {
  background: #fff8e1;
  border-color: #efb44f;
  color: #7a5200;
}

.enrollee-sync-button:disabled {
  cursor: default;
  opacity: 0.68;
}

.enrollee-sync-button i {
  color: #00897b;
  font-size: 20px;
}

.enrollee-sync-button.is-syncing i {
  animation: syncSpin 0.9s linear infinite;
}

.enrollee-sync-meta {
  align-items: flex-end;
  color: #607d8b;
  display: inline-flex;
  flex-direction: column;
  gap: 3px;
  line-height: 1.15;
  text-align: right;
}

.enrollee-sync-meta span {
  font-size: 0.67rem;
  font-weight: 800;
  text-transform: uppercase;
}

.enrollee-sync-meta strong {
  color: #263238;
  font-size: 0.86rem;
  font-weight: 800;
}

.enrollee-sync-status {
  background: #e0f2f1;
  border: 1px solid #80cbc4;
  border-bottom: 0;
  color: #00695c;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 9px 14px;
}

.enrollee-sync-status.has-error {
  background: #fff8e1;
  border-color: #f0cf6a;
  color: #7a5200;
}

.summary-label {
  color: #607d8b;
  flex: 0 0 auto;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.summary-content {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
  min-width: 0;
  text-align: right;
}

.enrollee-search-summary :deep(.chip),
.summary-chip {
  background: #ffffff !important;
  border: 1px solid #e7c967;
  border-radius: 999px;
  color: #263238 !important;
  font-size: 0.82rem;
  font-weight: 700;
  height: auto;
  line-height: 1.2;
  margin: 0 6px 6px 0;
  padding: 7px 12px;
}

.enrollee-search-summary :deep(.chip:first-child),
.summary-chip {
  background: #ffffff !important;
  border-color: #80cbc4;
  color: #263238 !important;
}

.enrollee-search-summary :deep(.chip i) {
  font-size: 15px;
  line-height: 1;
  vertical-align: -2px;
}

.enrollee-filter-card {
  background: #ffffff;
  border: 1px solid #c7d3d8;
  border-radius: 0 0 6px 6px;
  box-shadow: none;
  flex: none;
  overflow: visible;
  padding: 10px;
  width: 100%;
}

.enrollee-filter-card :deep(.section) {
  margin: 0;
  padding: 0;
}

.enrollee-filter-card :deep(.sectionx > br) {
  display: none;
}

.enrollee-filter-card :deep(form) {
  margin: 0;
}

.enrollee-filter-card :deep(.row) {
  align-items: stretch;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 10px;
  margin-bottom: 10px;
}

.enrollee-filter-card :deep(.row + .row) {
  border-top: 0;
}

.enrollee-filter-card :deep(.col) {
  border: 1px solid #c7d3d8;
  border-radius: 6px;
  min-width: 0;
  padding: 0 !important;
  width: 100% !important;
}

.enrollee-filter-card :deep(.col + .col) {
  border-left: 1px solid #c7d3d8;
}

.enrollee-filter-card :deep(nav) {
  background: transparent !important;
  box-shadow: none !important;
  height: auto;
  line-height: normal;
  min-height: 52px;
}

.enrollee-filter-card :deep(.input-field) {
  height: auto;
  margin-bottom: 0;
  margin-top: 0;
  min-height: 52px;
}

.enrollee-filter-card :deep(.nav-wrapper) {
  background: transparent;
  height: auto;
  line-height: normal;
  min-height: 52px;
}

.enrollee-filter-card :deep(input),
.enrollee-filter-card :deep(.select-wrapper input.select-dropdown) {
  background: transparent;
  border: none !important;
  box-sizing: border-box;
  color: #263238 !important;
  font-size: 1.02rem;
  font-weight: 700;
  height: 52px !important;
  line-height: 52px;
  margin: 0;
  padding: 0 42px 0 16px !important;
  width: 100%;
}

.enrollee-filter-card :deep(input:focus) {
  box-shadow: inset 0 -2px 0 #00897b !important;
}

.enrollee-filter-card :deep(.material-icons) {
  color: #00897b !important;
  height: 52px;
  line-height: 52px;
  right: 10px;
  top: 0;
}

.enrollee-filter-card :deep(.dropdown-content) {
  border-top: 1px solid #dfe7ea;
  box-shadow: none;
  margin: 0;
  max-height: 190px;
  overflow-y: auto;
  position: static !important;
  transform: none !important;
  width: 100% !important;
  z-index: 1;
}

.enrollee-filter-card :deep(.dropdown-content li > span) {
  color: #00897b;
  line-height: 1.35;
  padding: 12px 20px;
}

.enrollee-list-card {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  display: grid;
  gap: 0;
  margin-top: 18px;
  overflow: hidden;
  padding: 0;
}

.enrollee-table-head {
  background: #f5f7f8;
  border: 0;
  border-bottom: 1px solid #e3e8eb;
  border-radius: 0;
  color: #607d8b;
  display: grid;
  font-size: 0.69rem;
  font-weight: 700;
  gap: 18px;
  grid-template-columns: 52px minmax(270px, 1.7fr) minmax(210px, 1.2fr) minmax(180px, 1fr) minmax(110px, 0.65fr);
  padding: 12px 22px;
  text-transform: uppercase;
}

.enrollee-row-card {
  background: #ffffff;
  border: 0;
  border-bottom: 1px solid #d8e1e5;
  border-radius: 0;
  box-shadow: none;
  padding: 22px 22px;
  transition: background-color 0.18s ease, box-shadow 0.18s ease;
}

.enrollee-row-card:nth-child(even) {
  background: #fbfcfd;
}

.enrollee-list-card > .enrollee-row-card:first-child {
  border-top: 0;
}

.enrollee-list-card > .enrollee-row-card:last-child {
  border-bottom: 0;
}

.enrollee-table-head + .enrollee-row-card {
  border-top: 0;
}

.enrollee-row-card:hover {
  background: #f4f8f8;
}

.enrollee-row-grid {
  align-items: center;
  display: grid;
  gap: 18px;
  grid-template-columns: 52px minmax(270px, 1.7fr) minmax(210px, 1.2fr) minmax(180px, 1fr) minmax(110px, 0.65fr);
}

.enrollee-number {
  background: #f8fafb;
  border: 1px solid #dce6ea;
  border-radius: 6px;
  color: #607d8b;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 700;
  height: 30px;
  justify-content: center;
  margin: 0;
  width: 36px;
}

.enrollee-person h6 {
  align-items: center;
  color: #263238;
  display: flex;
  flex-wrap: wrap;
  font-size: 1.01rem;
  font-weight: 700;
  gap: 8px;
  line-height: 1.35;
  margin: 0;
}

.enrollee-contact-line {
  align-items: center;
  color: #78909c;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.84rem;
  font-weight: 500;
  gap: 8px;
  line-height: 1.35;
  margin-top: 7px;
}

.contact-dot {
  background: #cfd8dc;
  border-radius: 50%;
  display: inline-block;
  height: 5px;
  width: 5px;
}

.enrollee-id-line {
  align-items: center;
  color: #78909c;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.8rem;
  font-weight: 600;
  gap: 7px;
  line-height: 1.35;
  margin-top: 7px;
}

.enrollee-id-line span {
  color: #90a4ae;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
}

.enrollee-id-line strong {
  color: #455a64;
  font-weight: 700;
}

.enrollee-type-text {
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 600;
  line-height: 1.1;
  padding: 4px 7px;
}

.enrollee-type-text.is-principal {
  background: #d8ffd6;
  border: 1px solid #46d85a;
  color: #08731b;
}

.enrollee-type-text.is-spouse {
  background: #dbeafe;
  border: 1px solid #4f8cff;
  color: #104bb5;
}

.enrollee-type-text.is-dependant {
  background: #fff1b8;
  border: 1px solid #ffbf1a;
  color: #805400;
}

.enrollee-type-text.is-unclassified {
  background: #eef2f4;
  border: 1px solid #b8c4ca;
  color: #455a64;
}

.enrollee-client,
.enrollee-provider,
.enrollee-period {
  color: #263238;
  font-size: 0.94rem;
  font-weight: 400;
  line-height: 1.35;
  margin: 0;
}

.enrollee-provider,
.enrollee-period {
  color: #455a64;
}

.enrollee-period {
  align-items: flex-start;
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
}

.enrollee-period-flag {
  background: #fff7d6;
  border: 1px solid #f0cf6a;
  border-radius: 999px;
  color: #7a5200;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  padding: 5px 8px;
}

.enrollee-data-cell {
  border-left: 1px solid #dfe7ea;
  min-width: 0;
  padding-left: 18px;
}

.enrollee-data-cell > span:first-child {
  color: #78909c;
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1;
  margin-bottom: 7px;
  text-transform: uppercase;
}

.enrollee-empty-state {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  color: #607d8b;
  margin-top: 18px;
  padding: 34px 22px;
  text-align: center;
}

.enrollee-empty-state img {
  background: #f8fafb;
  border: 1px solid #e3e8eb;
  padding: 14px;
  width: 108px;
}

.enrollee-empty-state p {
  margin: 8px 0 0;
}

.line {
  margin-top: -45px;
}

.enrollee-progress {
  background: #e7ecef;
  border: 1px solid #c4d3d9;
  border-top: 0;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 14px 24px -24px rgba(38, 50, 56, 0.68);
  height: 4px;
  margin: 0;
  overflow: hidden;
}

.pointer {
  cursor: default;
}

/* base class */
.tab-custom {
  display: inline-block;
  cursor: pointer;
  margin: 0 0 -1px;
  padding: 15px 25px;
  font-weight: 600;
  text-align: center;
  color: #bbb;
  border: 1px solid transparent;
}

.tab-custom:hover {
  color: #000000 !important;
}

/* Removed
color: #000000;
border: 1px solid #ddd;
background-color: #d1f3df; */
.tab-custom-active {
  display: inline-block;
  cursor: pointer;
  margin: 0 0 -1px;
  padding: 15px 25px;
  font-weight: 600;
  text-align: center;
  border-bottom: 2px solid orange;
}

/* display */
.tab-custom-section {
  padding: 20px 0 0;
  border-top: 1px solid #ddd;
}

@media only screen and (max-width: 992px) {
  .enrollee-review-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .enrollee-title-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .enrollee-filter-card {
    flex-basis: auto;
    width: 100%;
  }

  .enrollee-row-grid {
    gap: 16px;
    grid-template-columns: minmax(0, 1.55fr) minmax(180px, 1fr) minmax(170px, 1fr);
  }

  .enrollee-data-cell {
    padding-left: 14px;
  }
}

@media only screen and (max-width: 600px) {
  .enrollee-review-head {
    padding: 22px;
  }

  .enrollee-title-block h5 {
    font-size: 1.45rem;
  }

  .enrollee-title-block p {
    font-size: 0.94rem;
  }

  .search-toggle {
    justify-content: space-between;
    width: 100%;
  }

  .enrollee-filter-card {
    padding: 10px;
  }

  .enrollee-sync-strip {
    align-items: stretch;
    flex-direction: column;
    gap: 9px;
  }

  .enrollee-sync-button {
    justify-content: center;
    width: 100%;
  }

  .enrollee-sync-actions {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }

  .enrollee-sync-meta {
    align-items: center;
    text-align: center;
  }

  .enrollee-search-summary {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px;
  }

  .summary-content {
    justify-content: flex-start;
    text-align: left;
  }

  .enrollee-list-card {
    background: transparent;
    border: 0;
    box-shadow: none;
    gap: 11px;
    overflow: visible;
  }

  .enrollee-row-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .enrollee-row-card {
    border: 1px solid #d8e1e5;
    border-radius: 8px;
    padding: 16px;
  }

  .enrollee-list-card > .enrollee-row-card:last-child {
    border-bottom: 1px solid #d8e1e5;
  }

  .enrollee-person h6 {
    font-size: 0.98rem;
  }

  .enrollee-data-cell {
    border-left: 0;
    border-top: 1px solid #edf2f4;
    padding-left: 0;
    padding-top: 12px;
  }

  .enrollee-client,
  .enrollee-provider,
  .enrollee-period {
    font-size: 0.9rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .enrollee-review-head,
  .enrollee-filter-shell,
  .enrollee-search-summary,
  .enrollee-progress,
  .enrollee-list-card,
  .enrollee-row-card,
  .enrollee-empty-state {
    animation: none;
  }

  .enrollee-sync-button.is-syncing i {
    animation: none;
  }
}

@keyframes syncSpin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
